import { PricingExperimentArm } from '@cognitoforms/types/server-types/plans/model/pricing-experiment-arm';

export { PricingExperimentArm };

/**
 * Cookie recording the arm the visitor was bucketed into, formatted as `campaign||arm`. Written by our
 * own bucketing decision (a client-side coin flip on marketing, a deterministic per-user hash for a
 * logged-in SPA user with no cookie yet) and read by the server on every request that quotes a price,
 * so the prices a visitor is shown, previewed, and charged all come from the arm they were bucketed into.
 */
export const pricingExperimentCookieName = 'c-pricing-experiment';

const pricingExperimentCookieDelimiter = '||';

/**
 * A visitor bucketed on one visit must not go unassigned when they sign up on a later one, so this
 * outlives a typical gap between a first visit and a return signup.
 */
const pricingExperimentCookieMaxAge = 100 * 24 * 60 * 60;

function readCookie(name: string): string | null {
	if (typeof document === 'undefined')
		return null;

	const match = document.cookie.split('; ').find(c => c.startsWith(`${name}=`));
	if (!match)
		return null;

	return match.slice(name.length + 1);
}

function parseArm(arm: string | undefined): PricingExperimentArm | null {
	if (arm?.toLowerCase() === 'variant')
		return PricingExperimentArm.Variant;

	if (arm?.toLowerCase() === 'control')
		return PricingExperimentArm.Control;

	return null;
}

/**
 * Reads whatever bucketing the visitor is carrying, whichever campaign it names. For reporting what a
 * visitor arrived with; anything quoting a price wants `readPricingExperimentArm` instead, which
 * ignores bucketing left over from a campaign that is no longer running.
 */
export function readPricingExperimentBucketing(): { campaign: string; arm: PricingExperimentArm } | null {
	const value = readCookie(pricingExperimentCookieName);
	if (value === null)
		return null;

	let parts: string[];
	try {
		parts = decodeURIComponent(value).split(pricingExperimentCookieDelimiter);
	}
	catch {
		return null;
	}
	if (parts.length !== 2)
		return null;

	const [campaign, arm] = parts;
	const bucketedArm = parseArm(arm);
	if (!campaign || bucketedArm === null)
		return null;

	return { campaign: campaign.toUpperCase(), arm: bucketedArm };
}

/**
 * Reads the arm recorded for `campaign`. An arm recorded for any other campaign is ignored, so a
 * stale cookie enrolls nobody.
 */
export function readPricingExperimentArm(campaign: string | null | undefined): PricingExperimentArm | null {
	if (!campaign)
		return null;

	const bucketing = readPricingExperimentBucketing();
	if (bucketing === null || bucketing.campaign.toLowerCase() !== campaign.toLowerCase())
		return null;

	return bucketing.arm;
}

/**
 * Records `arm` for `campaign` so the server quotes from it. Both arms are recorded: leaving control
 * unwritten would cost the experiment its baseline cohort.
 */
export function writePricingExperimentArm(campaign: string, arm: PricingExperimentArm): void {
	if (typeof document === 'undefined')
		return;

	const value = encodeURIComponent(`${campaign}${pricingExperimentCookieDelimiter}${arm}`);
	const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';

	document.cookie = `${pricingExperimentCookieName}=${value}; path=/; max-age=${pricingExperimentCookieMaxAge}; SameSite=Lax${secure}`;
}

/**
 * Flips a cryptographically random coin, weighted so `variantSplitPercent` of flips land on the
 * variant arm. Used only when no cookie has bucketed this visitor yet, so the flip happens at most
 * once per visitor per campaign.
 */
function flipCoin(variantSplitPercent: number): PricingExperimentArm {
	const buffer = new Uint32Array(1);
	crypto.getRandomValues(buffer);
	const roll = (buffer[0] / 2 ** 32) * 100;
	return roll < variantSplitPercent ? PricingExperimentArm.Variant : PricingExperimentArm.Control;
}

/**
 * Resolves the arm this visitor belongs to for `campaign`: the cookie already recorded for it when
 * present, otherwise a fresh coin flip weighted by `variantSplitPercent`. Synchronous and immediate —
 * there is nothing external to wait on — so callers can resolve the arm before first paint with no
 * flicker. A missing `variantSplitPercent` fails toward control rather than risk bucketing a visitor
 * into pricing the server never actually quoted.
 */
export function resolvePricingExperimentArm(campaign: string | null | undefined, variantSplitPercent: number | null | undefined): PricingExperimentArm | null {
	if (!campaign)
		return null;

	return readPricingExperimentArm(campaign) ?? flipCoin(variantSplitPercent ?? 0);
}
